import React, { useMemo, useState } from "react";
import { MIS_REPORT_CATEGORIES, MIS_REPORTS } from "./data/reportsRegistry";
import ReportTileCard from "./components/ReportTileCard";

const MISReportsHubPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredReports = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return MIS_REPORTS.filter((report) => {
      const matchesCategory =
        selectedCategory === "all" || report.category === selectedCategory;

      if (!matchesCategory) return false;
      if (!query) return true;

      const titleMatch = report.title.toLowerCase().includes(query);
      const shortTitleMatch = report.shortTitle.toLowerCase().includes(query);
      const descMatch = report.description.toLowerCase().includes(query);
      const categoryMatch = report.categoryLabel.toLowerCase().includes(query);

      return titleMatch || shortTitleMatch || descMatch || categoryMatch;
    });
  }, [selectedCategory, searchQuery]);

  const categoryCounts = useMemo(() => {
    const counts = { all: MIS_REPORTS.length };
    MIS_REPORTS.forEach((report) => {
      counts[report.category] = (counts[report.category] || 0) + 1;
    });
    return counts;
  }, []);

  return (
    <div className="space-y-4 pb-10">
      {/* Minimalist Executive Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200/70 pb-3 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl">
              MIS Reports
            </h1>
            <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-primary-600 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-950/40 dark:text-primary-400">
              Management Information System
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Centralized business intelligence and operational reporting across all company modules.
          </p>
        </div>

        <div className="text-xs text-slate-400 dark:text-slate-500 hidden sm:block">
          <span className="font-semibold text-slate-700 dark:text-slate-300">
            {MIS_REPORTS.length}
          </span>{" "}
          Standard Reports
        </div>
      </div>

      {/* Structured Filter & Search Toolbar */}
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-slate-200/90 bg-white p-2 shadow-sm dark:border-slate-800 dark:bg-slate-850">
        {/* Category Pills Bar */}
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-0.5">
          {MIS_REPORT_CATEGORIES.map((cat) => {
            const isSelected = selectedCategory === cat.id;
            const count = categoryCounts[cat.id] || 0;

            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-all duration-150 ${
                  isSelected
                    ? "bg-slate-900 text-white shadow-xs dark:bg-primary-500 dark:text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                }`}
              >
                <span>{cat.label}</span>
                <span
                  className={`rounded px-1.5 py-0.2 text-[10px] font-semibold ${
                    isSelected
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Structured Search Input */}
        <div className="relative min-w-[220px] sm:w-64 flex-shrink-0">
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reports..."
            className="w-full rounded-lg border border-slate-200/80 bg-slate-50 py-1.5 pl-8 pr-7 text-xs font-medium text-slate-800 placeholder-slate-400 transition-colors focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-slate-700/80 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:bg-slate-900"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>
      </div>

      {/* Reports Grid */}
      {filteredReports.length > 0 ? (
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredReports.map((report) => (
            <ReportTileCard key={report.id} report={report} />
          ))}
        </div>
      ) : (
        /* Minimal Empty State */
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center dark:border-slate-800 dark:bg-slate-850">
          <i className="fa-solid fa-magnifying-glass text-xl text-slate-400" />
          <h3 className="mt-2 text-sm font-semibold text-slate-800 dark:text-white">
            No reports match &quot;{searchQuery}&quot;
          </h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Try checking for spelling or resetting your category filter.
          </p>
          <button
            type="button"
            onClick={() => {
              setSearchQuery("");
              setSelectedCategory("all");
            }}
            className="mt-3 inline-flex items-center gap-1 rounded-md bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 transition-colors"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
};

export default MISReportsHubPage;
